import { useState, useRef, useCallback, useEffect } from "react";

interface UseRealtimeTranscriptionOptions {
  onTranscriptUpdate: (text: string, isFinal: boolean) => void;
  onError: (error: string) => void;
  onSessionEnd: () => void;
}

interface UseRealtimeTranscriptionReturn {
  startSession: (token: string, audioStream: MediaStream) => Promise<void>;
  stopSession: () => void;
  isConnected: boolean;
  isStreaming: boolean;
  accumulatedTranscript: string;
}

/**
 * Custom hook for managing real-time transcription with AssemblyAI.
 *
 * Handles:
 * - WebSocket connection lifecycle
 * - Audio streaming with ScriptProcessor (raw PCM16)
 * - Transcript accumulation (partial + final)
 * - Error handling and cleanup
 *
 * @example
 * const { startSession, stopSession, accumulatedTranscript, isStreaming } =
 *   useRealtimeTranscription({
 *     onTranscriptUpdate: (text, isFinal) => setTranscript(text),
 *     onError: (err) => showNotification(err),
 *     onSessionEnd: () => handleComplete()
 *   });
 */
export function useRealtimeTranscription(
  options: UseRealtimeTranscriptionOptions
): UseRealtimeTranscriptionReturn {
  const { onTranscriptUpdate, onError, onSessionEnd } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [accumulatedTranscript, setAccumulatedTranscript] = useState("");

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const isMountedRef = useRef(true);

  // Track partial transcript separately (gets replaced on each update)
  const partialTranscriptRef = useRef("");
  const finalTranscriptRef = useRef("");

  // Cleanup function
  const cleanup = useCallback(() => {
    // Close WebSocket (v3 uses {"type": "Terminate"} instead of {"terminate_session": true})
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "Terminate" }));
      wsRef.current.close();
    }
    wsRef.current = null;

    // Close AudioContext
    if (audioContextRef.current) {
      void audioContextRef.current.close();
    }
    audioContextRef.current = null;

    // Reset state
    if (isMountedRef.current) {
      setIsConnected(false);
      setIsStreaming(false);
    }

    partialTranscriptRef.current = "";
    finalTranscriptRef.current = "";
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  // Process audio using ScriptProcessor to get raw PCM16 samples
  const processAudioStream = useCallback((stream: MediaStream, ws: WebSocket) => {
    const audioContext = new AudioContext({ sampleRate: 16000 });
    const source = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(4096, 1, 1);

    audioContextRef.current = audioContext;

    processor.onaudioprocess = (e) => {
      if (ws.readyState !== WebSocket.OPEN) {
        return;
      }

      const inputData = e.inputBuffer.getChannelData(0);
      const int16Data = new Int16Array(inputData.length);

      // Convert Float32 samples to Int16 PCM
      for (let i = 0; i < inputData.length; i++) {
        const clamped = Math.max(-1, Math.min(1, inputData[i]));
        int16Data[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
      }

      // V3 API requires raw binary audio data (not JSON with base64)
      ws.send(int16Data.buffer);
    };

    source.connect(processor);
    processor.connect(audioContext.destination);

    return () => {
      processor.disconnect();
      source.disconnect();
      void audioContext.close();
    };
  }, []);

  const startSession = useCallback(
    // eslint-disable-next-line @typescript-eslint/require-await
    async (token: string, audioStream: MediaStream): Promise<void> => {
      try {
        // Reset state
        partialTranscriptRef.current = "";
        finalTranscriptRef.current = "";
        setAccumulatedTranscript("");

        // Connect to AssemblyAI WebSocket (v3 endpoint)
        const sampleRate = 16000;
        const encoding = "pcm_s16le"; // 16-bit signed little-endian PCM
        const wsUrl = `wss://streaming.assemblyai.com/v3/ws?sample_rate=${sampleRate}&encoding=${encoding}&token=${token}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        // WebSocket event handlers
        ws.onopen = () => {
          if (isMountedRef.current) {
            setIsConnected(true);
            setIsStreaming(true);
          }
        };

        ws.onmessage = (event: MessageEvent) => {
          // V3 API message structure
          const message = JSON.parse(event.data as string) as {
            type?: string;
            transcript?: string;
            utterance?: string;
            end_of_turn?: boolean;
            turn_is_formatted?: boolean;
            error?: string;
          };

          if (message.error) {
            onError(`Transcription error: ${message.error}`);
            cleanup();
            return;
          }

          // V3 uses "Turn" messages
          // - transcript: raw unformatted text
          // - utterance: formatted text with punctuation (when turn ends)
          // - end_of_turn: true when the turn/sentence is complete
          if (message.type === "Turn") {
            // Use utterance if available (formatted), otherwise transcript
            const text = message.utterance || message.transcript || "";

            if (message.end_of_turn && text) {
              // End of turn - commit the text
              finalTranscriptRef.current += text + " ";
              partialTranscriptRef.current = "";
              const fullText = finalTranscriptRef.current.trim();
              setAccumulatedTranscript(fullText);
              onTranscriptUpdate(fullText, true);
            } else if (text) {
              // Partial transcript - gets replaced each time
              partialTranscriptRef.current = text;
              const fullText = finalTranscriptRef.current + text;
              setAccumulatedTranscript(fullText);
              onTranscriptUpdate(fullText, false);
            }
          }
        };

        ws.onerror = () => {
          onError("Connection to transcription service failed");
          cleanup();
        };

        ws.onclose = () => {
          if (isMountedRef.current) {
            setIsConnected(false);
            setIsStreaming(false);
          }

          // Clean up audio context when WebSocket closes
          if (audioContextRef.current) {
            void audioContextRef.current.close();
            audioContextRef.current = null;
          }

          // Notify that session has ended
          onSessionEnd();
        };

        // Process audio stream directly using ScriptProcessor
        processAudioStream(audioStream, ws);
      } catch (err) {
        onError(err instanceof Error ? err.message : "Failed to start streaming session");
        cleanup();
      }
    },
    [cleanup, onError, onTranscriptUpdate, processAudioStream]
  );

  const stopSession = useCallback(() => {
    // Send ForceEndpoint to make AssemblyAI finalize the current utterance
    // Then send Terminate after a short delay to allow final transcript to arrive
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "ForceEndpoint" }));

      // Give AssemblyAI time to send the final Turn message, then terminate
      setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: "Terminate" }));
        }
      }, 500);
    }

    // Note: We don't call cleanup() here - we wait for onSessionEnd to be called
    // after the WebSocket closes following the Terminate message.
  }, []);

  return {
    startSession,
    stopSession,
    isConnected,
    isStreaming,
    accumulatedTranscript,
  };
}
